// src\themes\mad\elements\Header\SecondaryDrawer.tsx
'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { Heading } from '@/components/ui/Heading';
import { MenuItem } from '@/core/types/menuApi';
import { useDrawerStore } from '@/stores/drawerStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { Button } from '@/components/ui/button';

type SidebarDetailsProps = {
  selectedItem?: MenuItem;
};

export default function SecondaryDrawer({ selectedItem }: SidebarDetailsProps) {
  const { primaryOpen, secondaryOpen, setSecondaryOpen, setPrimaryOpen } =
    useDrawerStore();
  const { dictionary } = useDictionary()

  const sortedChildren = [...(selectedItem?.children ?? [])].sort((a, b) => {
    if (a.imageUrl && !b.imageUrl) return 1;
    if (!a.imageUrl && b.imageUrl) return -1;
    return 0;
  });

  function handleClose() {
    setSecondaryOpen(false);
    setPrimaryOpen(false);
  }

  return (
    <div
      className={`
    transition-all no-scrollbar duration-300 ease-in-out bg-white overflow-y-auto
    fixed top-0 h-full z-60
    w-full lg:w-80 
    ltr:left-0 rtl:right-0 
    lg:ltr:left-80 lg:rtl:right-80
    ltr:border-r rtl:border-l
    ${primaryOpen && secondaryOpen
          ? 'translate-x-0 opacity-100 pointer-events-auto'
          : 'ltr:-translate-x-full rtl:translate-x-full opacity-0 pointer-events-none'
        }
  `}
      role="navigation"
      aria-label="Secondary navigation drawer"
    >
      <div className="p-4 min-h-full">
        {selectedItem ? (
          <>
            <div className="flex justify-between items-center mb-6 h-12">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSecondaryOpen(false)}
                aria-label="Back to primary drawer"
              >
                <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
              </Button>

              <Link
                href={selectedItem.link || "#"}
                onClick={handleClose}
                className="flex items-center justify-between gap-1 hover:underline"
              >
                <Heading level={3}>
                  {dictionary.ui.pages.seeAll}
                </Heading>
                <ChevronRight className="w-4 h-4 rtl:rotate-180" />
              </Link>
            </div>
            <ul className="flex flex-col gap-y-3">
              {sortedChildren.map(child => (
                <li key={child.id} onClick={handleClose}>
                  <Link
                    href={child.link}
                    className="space-y-3 p-2 flex flex-col"
                  >

                    {child.imageUrl && (
                      <div className="relative w-full overflow-hidden rounded-sm bg-gray-100">
                        {/* ارتفاع رو حذف می‌کنیم و به Image اجازه می‌دیم خودش aspect ratio رو کنترل کنه */}
                        <Image
                          src={child.imageUrl}
                          alt={child.imageName ?? 'Untitled image'}
                          width={0}
                          height={0}
                          sizes="(max-width: 768px) 100vw, 300px"
                          className="w-full h-auto object-contain transition-all duration-300"
                          placeholder="blur"
                          blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F9PwAJKAmrXJ4ZfwAAAABJRU5ErkJggg=="
                        />
                      </div>
                    )}
                    <Heading
                      level={3}
                    >
                      {child.title}
                    </Heading>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </div>
  );
}
